/**
 * Validates avatar URL (absolute HTTP/HTTPS or relative path)
 * @param url - URL to validate
 * @returns Valid URL string or null if invalid
 * @example
 * validateAvatarUrl('https://example.com/avatar.jpg') // "https://example.com/avatar.jpg"
 * validateAvatarUrl('/images/avatar.jpg')             // "/images/avatar.jpg"
 * validateAvatarUrl('ftp://example.com')              // null (non-HTTP protocol)
 * validateAvatarUrl('www.google.com.///')             // null (malformed)
 * validateAvatarUrl('')                               // null
 */
export const validateAvatarUrl = (url: unknown): string | null => {
  if (typeof url !== 'string' || !url.trim()) return null;

  if (url.startsWith('/')) return url;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? url
      : null;
  } catch {
    return null;
  }
};

/**
 * Checks if a value is a valid avatar URL (for schema validation).
 * Accepts absolute HTTP/HTTPS URLs or relative paths starting with '/'.
 *
 * @param url - URL to validate
 * @returns `true` if valid avatar URL, `false` otherwise
 */
export const isValidAvatarUrl = (url: unknown): boolean =>
  validateAvatarUrl(url) !== null;

/**
 * Remove Google Image size parameter from URL (e.g., =s200, =w400)
 * @param url - URL string that may contain Google Image size parameters
 * @returns URL without size parameters, query parameters are preserved. Returns empty string if url is falsy.
 * @example
 * removeGoogleImageSizeParam('https://example.com/image.jpg=s200') // "https://example.com/image.jpg"
 * removeGoogleImageSizeParam('https://example.com/image.jpg=s200?other=value') // "https://example.com/image.jpg?other=value"
 */
export const removeGoogleImageSizeParam = (url?: string | null): string => {
  if (!url) return '';

  return url.replace(/=[sw]\d+(-[a-z]+)*(?=\?|$)/, '');
};

/**
 * Processes image URL for Remotion rendering.
 * - Removes Google Image size parameters (e.g., `=s200`, `=w400`)
 * - Validates URL format (HTTP/HTTPS or relative path)
 * - Wraps relative paths with staticFile for proper bundling
 *
 * @param url - Image URL to process (relative path or absolute URL)
 * @param staticFileWrapper - Function to wrap relative paths (e.g., Remotion's `staticFile`)
 * @returns Processed URL ready for Remotion's `<Img>` component, or `null` if invalid
 *
 * @example
 * // Local image
 * processImageUrlForRemotion('/images/avatar.jpg', staticFile)
 * // → staticFile('images/avatar.jpg')
 *
 * // External URL with Google size param
 * processImageUrlForRemotion('https://example.com/photo.jpg=s200', staticFile)
 * // → 'https://example.com/photo.jpg'
 *
 * // Invalid URL
 * processImageUrlForRemotion('ftp://invalid', staticFile) // → null
 */
export const processImageUrlForRemotion = (
  url: string | undefined,
  staticFileWrapper: (path: string) => string,
): string | null => {
  const validatedUrl = validateAvatarUrl(removeGoogleImageSizeParam(url));
  if (!validatedUrl) return null;

  return validatedUrl.startsWith('/')
    ? staticFileWrapper(validatedUrl.slice(1))
    : validatedUrl;
};
